import type { PostMetadataGroupScore } from '@/lib/db/types';
import { scoreToParLabel } from '@/lib/scoring/played-par';

interface Props {
  groupScores: PostMetadataGroupScore[];
  coursePar?: number | null;
  compact?: boolean;
  className?: string;
  highlightMemberId?: string;
  useFullNames?: boolean;
}

function grossTotal(scores: PostMetadataGroupScore['scores']): number | null {
  if (!scores.length) return null;
  return scores.reduce((sum, h) => sum + h.strokes, 0);
}

export function GroupScoresRecap({
  groupScores,
  coursePar,
  compact,
  className,
  highlightMemberId,
  useFullNames,
}: Props) {
  if (!groupScores.length) return null;

  const rows = groupScores
    .map((g) => ({
      ...g,
      gross: grossTotal(g.scores),
    }))
    .filter((g) => g.gross != null)
    .sort((a, b) => (a.gross ?? 999) - (b.gross ?? 999));

  if (!rows.length) return null;

  const shellClass = className ?? 'mx-4';

  return (
    <div className={`${shellClass} rounded-[14px] bg-navy text-paper ${compact ? 'mb-2 p-3' : 'mb-3 p-[18px]'}`}>
      <div className="mb-2.5 text-[10px] font-extrabold tracking-[0.15em] text-gold-bright">
        GROUP SCORECARD
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => {
          const parLabel = coursePar != null && row.gross != null
            ? scoreToParLabel(row.gross, coursePar)
            : null;
          const isYou = highlightMemberId === row.id;
          const displayName = useFullNames ? row.name : row.name.split(' ')[0];
          return (
            <div
              key={row.id}
              className={`flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 ${
                isYou ? 'bg-white/10 ring-1 ring-gold-bright/40' : ''
              }`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 font-mono text-[10px] font-bold text-gold-bright">
                  {index + 1}
                </span>
                <div className="min-w-0 truncate font-serif text-sm font-semibold">
                  {displayName}
                  {row.guest && (
                    <span className="ml-1 text-[9px] font-bold text-gold-bright">GUEST</span>
                  )}
                  {isYou && (
                    <span className="ml-1 text-[9px] font-bold text-gold-bright">YOU</span>
                  )}
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-2xl font-bold leading-none">{row.gross}</span>
                {parLabel && (
                  <span className="rounded-md bg-white/12 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gold-bright">
                    {parLabel}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
